import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Target } from "lucide-react"
import { studentPracticePaperHref } from "../../../../component/student/config/studentNavItems"
import type {
  DashboardGoal,
  DashboardGoalPaper,
  NewDashboardGoalInput,
} from "../types"
import { sliceForShowMore } from "../utils/sliceForShowMore"
import ConfirmModal from "../../components/ConfirmModal"
import AddGoalModal from "./AddGoalModal"
import DashboardGoalCard from "./DashboardGoalCard"
import DashboardShowMoreButton from "./DashboardShowMoreButton"
import "./DashboardGoalsPanel.style.scss"

interface DashboardGoalsPanelProps {
  goals: DashboardGoal[]
  loading?: boolean
  error?: string | null
  isSaving?: boolean
  onAddGoal: (goal: NewDashboardGoalInput) => Promise<void> | void
}

const VISIBLE_GOAL_COUNT = 2

const DashboardGoalsPanel = ({
  goals,
  loading = false,
  error = null,
  isSaving = false,
  onAddGoal,
}: DashboardGoalsPanelProps) => {
  const navigate = useNavigate()
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [pendingAbandoned, setPendingAbandoned] =
    useState<DashboardGoalPaper | null>(null)
  const visibleGoals = sliceForShowMore(goals, showAll, VISIBLE_GOAL_COUNT)

  return (
    <section className="dashboard-goals-panel">
      <div className="dashboard-goals-panel__head">
        <h3>
          <Target size={20} />
          Upcoming Revision Goals
        </h3>
        <button type="button" onClick={() => setIsAddOpen(true)}>
          Add Goal
        </button>
      </div>
      <div className="dashboard-goals-panel__list">
        {loading ? (
          <p className="dashboard-goals-panel__empty">Loading goals…</p>
        ) : error ? (
          <p className="dashboard-goals-panel__empty">{error}</p>
        ) : goals.length === 0 ? (
          <p className="dashboard-goals-panel__empty">
            No goals yet — add one to plan your revision.
          </p>
        ) : (
          visibleGoals.map((goal) => (
            <DashboardGoalCard
              key={goal.id}
              goal={goal}
              onAbandonedPaper={setPendingAbandoned}
            />
          ))
        )}
      </div>
      {!loading && !error ? (
        <DashboardShowMoreButton
          expanded={showAll}
          extraCount={goals.length - VISIBLE_GOAL_COUNT}
          onToggle={() => setShowAll((open) => !open)}
        />
      ) : null}

      <AddGoalModal
        isOpen={isAddOpen}
        isSaving={isSaving}
        onClose={() => setIsAddOpen(false)}
        onSubmit={onAddGoal}
      />

      <ConfirmModal
        isOpen={Boolean(pendingAbandoned)}
        title="This paper was abandoned"
        message={
          pendingAbandoned
            ? `“${pendingAbandoned.title}” is an abandoned paper. Do you want to retake it?`
            : "This is an abandoned paper. Do you want to retake it?"
        }
        confirmLabel="Retake"
        cancelLabel="Cancel"
        onCancel={() => setPendingAbandoned(null)}
        onConfirm={() => {
          if (!pendingAbandoned) return
          const href = studentPracticePaperHref(pendingAbandoned.id, true)
          setPendingAbandoned(null)
          navigate(href)
        }}
      />
    </section>
  )
}

export default DashboardGoalsPanel
